import { Box, Typography } from '@mui/material'

import PlainHeader from '@ecm/ui/component/block/PlainHeader'
import Anchor from '@ecm/ui/element/Anchor'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'
import DefaultLayout from '@ecm/ui/layout/Default'

import { ComplexDetailTable, DetailRow, DetailTable } from './detail'

export const PricingDetail2020Page = () => (
  <DefaultLayout
    hideFinalCta
    seoTitle="Detailed Pricing | Earth Class Mail"
    seoDescription="Detailed Pricing"
  >
    <PlainHeader title="Detailed Pricing" />
    <MainContainer>
      <Box paddingY={{ md: '2em', xs: '1em' }}>
        <Typography variant="h3" component="h2">
          Plans sold from June 7, 2020 to May 31, 2023
        </Typography>
        <Typography variant="p2">
          If you are subscribed to a MailBox or MailRoom plan sold on or after June 7th, 2020
          through May 31, 2023, please consult the pricing details below.
        </Typography>
        <DetailTable heading="Addresses">
          <DetailRow label="Cost per additional address beyond plan limits" value="$30 per month" />
        </DetailTable>

        <Typography variant="p4">
          Personal and Shared MailBox plans require using our Beaverton, OR address.
        </Typography>

        <DetailTable heading="Recipients">
          <DetailRow
            label="Cost per additional recipient beyond plan limits"
            value="$5 per month"
          />
        </DetailTable>

        <DetailTable heading="Incoming Mail">
          <DetailRow
            label="Starter, Essentials and Deluxe MailBox plans"
            value="Mail received over plan limits billed as $3 per item."
          />
          <DetailRow
            label="Personal, Shared and Premium MailBox plans"
            value="Mail received over plan limits billed as $2 per item."
          />
          <DetailRow
            label="Startup MailRoom"
            value="Mail received over plan limits billed as $1.75 per item."
          />
          <DetailRow
            label="Business MailRoom"
            value="Mail received over plan limits billed as $1.50 per item."
          />
          <DetailRow
            label="Business Address"
            value="Each mail item received billed as $1 per item."
          />
        </DetailTable>

        <DetailTable heading="Content Scans">
          <DetailRow
            label="MailBox and MailRoom Plans"
            value="You won't be charged extra for scanning as long as your mailed documents are 20 pages or less."
          />
          <DetailRow
            label="Business Address"
            value="Content scans billed as $4 per item plus per page fees for documents that are more than 20 pages."
          />
        </DetailTable>
        <Typography variant="p3" color={Color.saint.normal}>
          $0.10 per page for black and white scanned documents over 20 pages.
        </Typography>
        <Typography variant="p3" color={Color.saint.normal}>
          $0.15 per page for color scanned documents over 20 pages.
        </Typography>
      </Box>

      <Box paddingY={{ md: '2em', xs: '1em' }}>
        <Typography variant="h3" component="h2">
          Plans sold prior to June 7th, 2020
        </Typography>
        <Typography variant="p2">
          If you are on a plan sold before June 7, 2020, please consult the tables below for pricing
          information.
        </Typography>

        <ComplexDetailTable
          heading="Addresses"
          subheading="The following pricing structure pertains to additional addresses not included in your current plan"
        >
          <tr>
            <th>Address type</th>
            <th>Tier</th>
            <th>Price</th>
          </tr>
          <tr>
            <td className="label" rowSpan={3}>
              PO Box
            </td>
            <td>1st</td>
            <td>$20 per month per address.</td>
          </tr>
          <tr>
            <td>2nd-5th</td>
            <td>$10 per month per address</td>
          </tr>
          <tr>
            <td>6th+</td>
            <td>$5 per month per address</td>
          </tr>

          <tr>
            <td className="label" rowSpan={3}>
              Street address
            </td>
            <td>1st</td>
            <td>$40 per month per address.</td>
          </tr>
          <tr>
            <td>2nd-5th</td>
            <td>$20 per month per address</td>
          </tr>
          <tr>
            <td>6th+</td>
            <td>$10 per month per address</td>
          </tr>

          <tr>
            <td className="label" rowSpan={3}>
              Premium street address, San Francisco, New York
            </td>
            <td>1st</td>
            <td>$60 per month per address.</td>
          </tr>
          <tr>
            <td>2nd-5th</td>
            <td>$30 per month per address</td>
          </tr>
          <tr>
            <td>6th+</td>
            <td>$15 per month per address</td>
          </tr>
        </ComplexDetailTable>

        <DetailTable heading="Recipients">
          <DetailRow label="All plans" value="$0 per month." />
        </DetailTable>

        <DetailTable heading="Incoming Mail">
          <DetailRow
            label="Starter"
            value="15 mail items included in plan. Additional items billed as $0.50 per item."
          />
          <DetailRow
            label="Business Address"
            value="0 mail items included in plan. All items billed as $1 per item."
          />
          <DetailRow
            label="All other plans sold prior to June 7th, 2020"
            value="Unlimited mail items included."
          />
        </DetailTable>

        <DetailTable heading="Content Scans">
          <DetailRow
            label="Business Address"
            value="0 items included in plan. All content scans $4 per item."
          />
          <DetailRow
            label="Starter"
            value="10 items included in plan. Additional content scans $3 per item."
          />
          <DetailRow
            label="Professional"
            value="50 items included in plan. Additional content scans $3 per item."
          />
          <DetailRow
            label="Premium"
            value="50 items included in plan. Additional content scans $3 per item."
          />
          <DetailRow label="Other plans">
            <Anchor href="/contact">Contact customer support</Anchor>
          </DetailRow>
          <DetailRow label="Per page fees">
            <Typography variant="p3">
              For all content scans regardless of plan type, the first 20 pages of each scan are
              included.
            </Typography>
            <Typography variant="p3">
              $0.10 per page for black and white scanned documents over 20 pages; $0.15 per page for
              color scanned documents over 20 pages.
            </Typography>
          </DetailRow>
        </DetailTable>
      </Box>

      <Box paddingY={{ md: '2em', xs: '1em' }}>
        <Typography variant="h3" component="h2">
          Standard Fees and Overages for All Plans
        </Typography>

        <DetailTable
          heading="Mail Storage"
          subheading="All of our plans include physical mail storage for 30 days, and free cloud storage of digitized mail. Storage after 30 days incurs a small fee."
        >
          <DetailRow label="Retention policy" value="30 days" />
          <DetailRow label="Letter storage" value="$1.50 per month after 30 days" />
          <DetailRow label="Parcel storage" value="$15.00 per pound per month after 10 days" />
        </DetailTable>

        <DetailTable heading="Check Deposit Add-On">
          <DetailRow
            label="All new subscriptions after June 7th, 2020"
            value="$39 per month; 5 checks included in plan. Additional check deposits $2 each."
          />
          <DetailRow
            label="Legacy Check Deposit Subscription"
            value="$35 per month; Check deposits $2 each."
          />
          <DetailRow label="Expedited check deposit requests" value="$20 per check." />
        </DetailTable>

        <ComplexDetailTable
          heading="Shipping"
          subheading="We will gladly process forwarding shipments for you that do not exceed 150 pounds in weight and 15.5 inches x 24 inches x 18.5 inches in size."
        >
          <tr>
            <th>Shipping Type</th>
            <th>Plans</th>
            <th>Shipping Box / Handling</th>
            <th>Box Contents</th>
          </tr>

          <tr>
            <td className="label" rowSpan={2}>
              Domestic
            </td>
            <td>Starter, Legacy</td>
            <td>$3.00 per shipment plus postage.</td>
            <td>$1 per item.</td>
          </tr>
          <tr>
            <td>Other</td>
            <td>$1.25 per shipment plus postage.</td>
            <td>$1 per item.</td>
          </tr>

          <tr>
            <td className="label" rowSpan={2}>
              International
            </td>
            <td>Starter, Legacy</td>
            <td>$9.00 per shipment plus postage.</td>
            <td>$1 per item.</td>
          </tr>
          <tr>
            <td>Other</td>
            <td>$5 per shipment plus postage.</td>
            <td>$1 per item.</td>
          </tr>

          <tr>
            <td className="label">Expedited shipment</td>
            <td>All</td>
            <td>$10 per shipment.</td>
            <td>$1 per item.</td>
          </tr>
        </ComplexDetailTable>

        <DetailTable heading="Special packaging">
          <DetailRow label="Medium box" value="$5" />
          <DetailRow label="Large box" value="$10" />
          <DetailRow label="Standard custom packaging" value="$12.50 per 10 minutes." />
          <DetailRow label="Expedited custom packaging" value="$25 per 10 minutes." />
        </DetailTable>

        <DetailTable heading="Special requests">
          <DetailRow label="Expedited incoming mail item" value="$15 per item." />
          <DetailRow label="Expedited scanning" value="$15 per scanned item." />
          <DetailRow label="Special Handling" value="$12.50 per 10 minutes." />
          <DetailRow
            label="Heavy incoming mail item"
            value="Items over 20lbs will not be accepted."
          />
          <DetailRow
            label="Oversized package"
            value={`Items exceeding 19" x 14" x 12" will not be accepted.`}
          />
        </DetailTable>

        <DetailTable heading="Secure shredding">
          <DetailRow label="All plans" value="Free" />
        </DetailTable>
      </Box>

      <Box paddingY={{ md: '2em', xs: '1em' }}>
        <Typography variant="h5" component="h3">
          Cancellation and Reactivation Fees
        </Typography>
        <Typography variant="p3">
          All Earth Class Mail plans carry an associated $50 cancellation fee that covers the cost
          of handling your mail for an additional 6 months after closing an account.
        </Typography>
        <Typography variant="p3">
          A reactivation fee of $99 applies to reactivating a closed account. Reactivation is only
          available for accounts closed within 90 days.
        </Typography>
      </Box>
    </MainContainer>
  </DefaultLayout>
)

export default PricingDetail2020Page
